import { DisplayDataset } from '@cibo/core'
import { MapTheme } from '@cibo/mapgl'
import { useDisplay } from '@cibo/ui'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import { CircularProgress, Stack, useTheme } from '@mui/material'
import { StyleFunction } from 'mapbox-gl'
import { useMemo } from 'react'
import { Layer, Source } from 'react-map-gl/dist/es5'
import { LandReportMapDataLayersProps } from './types'

interface LandReportMapDataLayerProps
  extends Pick<LandReportMapDataLayersProps, 'field' | 'zoomThreshold'> {
  dataset: DisplayDataset
}

export const DataLayer = ({ dataset, field, zoomThreshold }: LandReportMapDataLayerProps) => {
  const { palette } = useTheme<MapTheme>()
  const display = useDisplay(dataset, field.resourceId)

  const fillOpacity = useMemo(
    () =>
      !!display.isLoading || !!display.isError
        ? zoomThreshold
          ? ([
              'interpolate',
              ['linear'],
              ['zoom'],
              zoomThreshold,
              0,
              zoomThreshold + 0.75,
              0.3,
            ] as StyleFunction)
          : 0.3
        : zoomThreshold
        ? ([
            'interpolate',
            ['linear'],
            ['zoom'],
            zoomThreshold,
            0,
            zoomThreshold + 0.75,
            1,
          ] as StyleFunction)
        : 1,
    [zoomThreshold, display.isLoading, display.isError]
  )

  const fillColor = useMemo(
    () =>
      !!display.isLoading || !!display.isError
        ? palette.grey?.[800]
        : (['get', 'fill-color'] as StyleFunction),
    [display.isLoading, display.isError]
  )

  const opacity = useMemo(
    () =>
      zoomThreshold
        ? ([
            'interpolate',
            ['linear'],
            ['zoom'],
            zoomThreshold,
            0,
            zoomThreshold + 0.75,
            1,
          ] as StyleFunction)
        : 1,
    [zoomThreshold]
  )

  return (
    <>
      <Source type="geojson" data={display.data?.geojson ? display.data.geojson : field.feature}>
        <Layer
          id={`${dataset}-fill`}
          type="fill"
          paint={{
            'fill-color': fillColor,
            'fill-opacity': fillOpacity,
          }}
        />
      </Source>
      <Source type="geojson" data={field.feature}>
        <Layer
          id={`${dataset}-boundary`}
          type="line"
          //@ts-ignore problem with extending PaletteOptions
          paint={{ 'line-color': palette.secondary.dark, 'line-width': 4, 'line-opacity': opacity }}
        />
      </Source>
      {!!display.isLoading && (
        <Stack alignItems="center" justifyContent="center" flex={1} height="100%">
          <CircularProgress />
        </Stack>
      )}
      {!!display.isError && (
        <Stack alignItems="center" justifyContent="center" flex={1} height="100%">
          <ReportProblemRoundedIcon color="error" fontSize="large" />
        </Stack>
      )}
    </>
  )
}
